/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import getIn from 'lodash/get';
import setIn from 'lodash/set';

import { formatStr } from '../../../helpers/string-helpers';

import type { FormErrors } from '../interfaces';

export const immediatelyValidateFlag = '!immediately';

const formatString = (tmpl: string, tmplSubstring: string): string => formatStr(tmpl.replace(/{{length}}/, '{0}'), tmplSubstring);

export class FormValidator<T> {
    value: T;
    errors: FormErrors<T>;

    constructor(value: T) {
        this.value = value;
        this.errors = {};
    }

    validate(path: keyof T | string): FieldValidator {
        return new FieldValidator(this.value, path as string, this.errors);
    }

    result(): FormErrors<T> {
        return this.errors;
    }

    get(path: string): any {
        return getIn(this.value, path);
    }
}

class FieldValidator {
    value: any;
    path: string;
    errors: FormErrors<any>;

    constructor(value: any, path: string, errors: FormErrors<any>) {
        this.value = value;
        this.path = path;
        this.errors = errors;
    }

    string(): StringValidator {
        return new StringValidator(getIn(this.value, this.path), this.path, this.errors);
    }

    number(): NumberValidator {
        return new NumberValidator(getIn(this.value, this.path), this.path, this.errors);
    }

    date(): DateValidator {
        return new DateValidator(getIn(this.value, this.path), this.path, this.errors);
    }

    password(hasValueMask: string | null = null) {
        return new PasswordValidator(getIn(this.value, this.path), this.path, this.errors, hasValueMask);
    }

    object(): ObjectValidator {
        return new ObjectValidator(getIn(this.value, this.path), this.path, this.errors);
    }
}

class BaseTypeValidator<T> {
    protected value: T;
    protected path: string;
    protected errors: FormErrors<any>;
    protected validateEnabled = true;

    constructor(value: T, path: string, errors: FormErrors<any>) {
        this.value = value;
        this.path = path;
        this.errors = errors;
    }

    check(f: (value: T) => string | void): BaseTypeValidator<T> {
        if (!this.validateEnabled) {
            return this;
        }

        const message = f(this.value);
        if (typeof message === 'string' && message.length > 0) {
            setIn(this.errors, this.path, message);
            this.validateEnabled = false;
        }

        return this;
    }
}

class StringValidator extends BaseTypeValidator<string> {
    defaultRequiredErrorMessage = 'This field is required';
    defaultMaxLenErrorMessage = `Text length must be less than {0}${immediatelyValidateFlag}`;
    defaultRegexpErrorMessage = 'Text does not match the pattern';

    required(errorMessage?: string): StringValidator {
        if (this.validateEnabled && (!this.value || (typeof this.value === 'string' && !this.value.trim()))) {
            setIn(this.errors, this.path, errorMessage || this.defaultRequiredErrorMessage);
            this.validateEnabled = false;
        }

        return this;
    }

    maxLength(max: number, errorMessage?: string): StringValidator {
        if (this.validateEnabled && typeof this.value === 'string' && this.value.length > max) {
            const message = formatString(errorMessage || this.defaultMaxLenErrorMessage, max.toString());
            setIn(this.errors, this.path, message);
            this.validateEnabled = false;
        }

        return this;
    }

    match(regex: RegExp, errorMessage?: string): StringValidator {
        if (this.validateEnabled && typeof this.value === 'string' && !regex.test(this.value)) {
            const message = errorMessage || this.defaultRegexpErrorMessage;
            setIn(this.errors, this.path, message);
            this.validateEnabled = false;
        }

        return this;
    }

    check(f: (value: string) => string | void): StringValidator {
        return super.check(f) as StringValidator;
    }
}

class PasswordValidator extends StringValidator {
    hasValueMask: string | null;

    constructor(value: string, path: string, errors: FormErrors<any>, hasValueMask: string | null = null) {
        super(value, path, errors);
        this.hasValueMask = hasValueMask;
    }

    required(errorMessage?: string): StringValidator {
        if (this.validateEnabled && !this.value && this.value !== this.hasValueMask) {
            setIn(this.errors, this.path, errorMessage || this.defaultRequiredErrorMessage);
            this.validateEnabled = false;
        }

        return this;
    }
}

class NumberValidator extends BaseTypeValidator<number> {
    defaultRequiredErrorMessage = 'This field is required';
    defaultMaxErrorMessage = `Max value is {0}${immediatelyValidateFlag}`;
    defaultMinErrorMessage = `Min value is {0}${immediatelyValidateFlag}`;
    defaultLessThenErrorMessage = `Value must be less than {0}${immediatelyValidateFlag}`;
    defaultMoreThenErrorMessage = `Value must be more than {0}${immediatelyValidateFlag}`;

    required(errorMessage?: string): NumberValidator {
        if (this.validateEnabled && (this.value === null || this.value === undefined)) {
            setIn(this.errors, this.path, errorMessage || this.defaultRequiredErrorMessage);
            this.validateEnabled = false;
        }

        return this;
    }

    max(max: number, errorMessage?: string): NumberValidator {
        if (this.validateEnabled && typeof this.value === 'number' && this.value > max) {
            const message = formatString(errorMessage || this.defaultMaxErrorMessage, max.toString());
            setIn(this.errors, this.path, message);
            this.validateEnabled = false;
        }

        return this;
    }

    min(min: number, errorMessage?: string): NumberValidator {
        if (this.validateEnabled && typeof this.value === 'number' && this.value < min) {
            const message = formatString(errorMessage || this.defaultMinErrorMessage, min.toString());
            setIn(this.errors, this.path, message);
            this.validateEnabled = false;
        }

        return this;
    }

    check(f: (value: number) => string): NumberValidator {
        return super.check(f) as NumberValidator;
    }
}

class DateValidator extends BaseTypeValidator<Date> {
    defaultRequiredErrorMessage = 'This field is required';
    defaultMaxErrorMessage = 'Max value is {0}';
    defaultMinErrorMessage = 'Min value is {0}';

    required(errorMessage?: string): DateValidator {
        if (this.validateEnabled && (this.value === null || this.value === undefined)) {
            setIn(this.errors, this.path, errorMessage || this.defaultRequiredErrorMessage);
            this.validateEnabled = false;
        }

        return this;
    }

    max(max: Date, errorMessage?: string): DateValidator {
        if (this.validateEnabled && typeof this.value === 'object' && this.value > max) {
            const message = formatString(errorMessage || this.defaultMaxErrorMessage, max.toString());
            setIn(this.errors, this.path, message);
            this.validateEnabled = false;
        }

        return this;
    }

    min(min: Date, errorMessage?: string): DateValidator {
        if (this.validateEnabled && typeof this.value === 'object' && this.value < min) {
            const message = formatString(errorMessage || this.defaultMinErrorMessage, min.toString());
            setIn(this.errors, this.path, message);
            this.validateEnabled = false;
        }

        return this;
    }

    check(f: (value: Date) => string): DateValidator {
        return super.check(f) as DateValidator;
    }
}

class ObjectValidator extends BaseTypeValidator<Object> {
    defaultRequiredErrorMessage = 'This field is required';

    required(errorMessage?: string): ObjectValidator {
        if (this.validateEnabled && (this.value === null || this.value === undefined)) {
            setIn(this.errors, this.path, errorMessage || this.defaultRequiredErrorMessage);
            this.validateEnabled = false;
        }

        return this;
    }

    check(f: (value: Object) => string | void): ObjectValidator {
        return super.check(f) as ObjectValidator;
    }
}
